import { getDesktopShortcut } from './desktopShortcuts'

export interface MarkdownEdit {
  body: string
  selectionStart: number
  selectionEnd: number
}

export type EditorShortcut = 'bold' | 'italic' | 'underline' | 'heading' | 'link' | 'undo' | 'redo' | 'new' | 'done' | 'suppress'

export type EditorChangeKind = 'typing' | 'format'

export interface EditorHistory {
  past: string[]
  future: string[]
  lastChangeAt: number
  lastChangeKind: EditorChangeKind | null
}

export interface EditorHistoryResult {
  body: string
  history: EditorHistory
}

const TYPING_GROUP_WINDOW_MS = 750
const MAX_HISTORY_ENTRIES = 100

export function createEditorHistory(): EditorHistory {
  return {
    past: [],
    future: [],
    lastChangeAt: 0,
    lastChangeKind: null,
  }
}

export function recordEditorChange(
  history: EditorHistory,
  currentBody: string,
  kind: EditorChangeKind,
  changedAt = Date.now(),
): EditorHistory {
  const continuesTypingGroup =
    kind === 'typing' &&
    history.lastChangeKind === 'typing' &&
    changedAt - history.lastChangeAt <= TYPING_GROUP_WINDOW_MS
  return {
    past: continuesTypingGroup
      ? history.past
      : [...history.past, currentBody].slice(-MAX_HISTORY_ENTRIES),
    future: [],
    lastChangeAt: changedAt,
    lastChangeKind: kind,
  }
}

export function undoEditorChange(
  history: EditorHistory,
  currentBody: string,
): EditorHistoryResult | null {
  const body = history.past.at(-1)
  if (body === undefined) return null
  return {
    body,
    history: {
      past: history.past.slice(0, -1),
      future: [...history.future, currentBody],
      lastChangeAt: 0,
      lastChangeKind: null,
    },
  }
}

export function redoEditorChange(
  history: EditorHistory,
  currentBody: string,
): EditorHistoryResult | null {
  const body = history.future.at(-1)
  if (body === undefined) return null
  return {
    body,
    history: {
      past: [...history.past, currentBody].slice(-MAX_HISTORY_ENTRIES),
      future: history.future.slice(0, -1),
      lastChangeAt: 0,
      lastChangeKind: null,
    },
  }
}

export function getEditorShortcut(
  key: string,
  metaKey: boolean,
  ctrlKey: boolean,
  shiftKey = false,
  altKey = false,
  code = '',
): EditorShortcut | null {
  switch (getDesktopShortcut({ code, metaKey, ctrlKey, shiftKey, altKey })) {
    case 'toggle-bold': return 'bold'
    case 'toggle-italic': return 'italic'
    case 'toggle-underline': return 'underline'
    case 'toggle-heading': return 'heading'
    case 'edit-link': return 'link'
    case 'new-note': return 'new'
  }
  if (altKey || (metaKey && ctrlKey)) return null
  const commandModifier = metaKey || ctrlKey
  const normalizedKey = key.toLowerCase()
  if (commandModifier && normalizedKey === 'z') return shiftKey ? 'redo' : 'undo'
  if (commandModifier && normalizedKey === 'y') return 'redo'
  if (commandModifier && !shiftKey && normalizedKey === 'b') return 'bold'
  if (commandModifier && !shiftKey && normalizedKey === 'i') return 'italic'
  if (commandModifier && !shiftKey && normalizedKey === 'u') return 'suppress'
  if ((commandModifier && key === 'Enter') || key === 'Escape') return 'done'
  return null
}

export function wrapMarkdownSelection(
  body: string,
  start: number,
  end: number,
  prefix: string,
  suffix: string,
  placeholder: string,
): MarkdownEdit {
  const selected = body.slice(start, end) || placeholder
  const insertion = `${prefix}${selected}${suffix}`
  return {
    body: `${body.slice(0, start)}${insertion}${body.slice(end)}`,
    selectionStart: start + prefix.length,
    selectionEnd: start + prefix.length + selected.length,
  }
}
